import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import WealthboxTaskDetailModal from '../shared/WealthboxTaskDetailModal'
import CreateWealthboxTaskModal from '../shared/CreateWealthboxTaskModal'
import WealthboxTaskRow from '../shared/WealthboxTaskRow'

// Caches that get busted when a task is created / modified / completed /
// deleted via the modals below. Includes both the list's own cache key
// (`list-preset`) and the metric's (`metric-preset` + `task-list-preset`) so
// a wealthbox-metric tile on the same page reflects the change immediately.
const TASK_INVALIDATE_KEYS = [
  [QUERY_KEYS.wealthboxDomainQuery, 'list-preset'],
  [QUERY_KEYS.wealthboxDomainQuery, 'metric-preset'],
  [QUERY_KEYS.wealthboxDomainQuery, 'task-list-preset']
]

function WealthboxTasksContent ({ items, limit, config, contactId, createOpen, onCloseCreate }) {
  const [selectedTask, setSelectedTask] = useState(null)
  const enableEdit = config?.enableEdit ?? false
  const formMode = config?.formMode ?? 'simple'
  const scope = config?.scope || 'client'

  // Sort by due_date ascending so the soonest deadlines surface first; tasks
  // without a due_date sink to the bottom. Slice to `limit` after the sort so
  // we respect the admin's display cap even when we asked for more items to
  // give the sort headroom (see useWealthboxListPreset's per_page logic).
  const sorted = useMemo(() => {
    const arr = Array.isArray(items) ? [...items] : []
    arr.sort((a, b) => {
      const da = a.due_date ? dayjs(a.due_date).valueOf() : Number.POSITIVE_INFINITY
      const db = b.due_date ? dayjs(b.due_date).valueOf() : Number.POSITIVE_INFINITY
      return da - db
    })
    return arr.slice(0, limit)
  }, [items, limit])

  const handleRowClick = useCallback((task) => setSelectedTask(task), [])
  const handleCloseDetail = useCallback(() => setSelectedTask(null), [])

  // IMPORTANT: do NOT early-return on empty state - the create modal must stay
  // mounted so the parent's Create button (which only toggles `createOpen`)
  // can actually open it. See WealthboxEventsContent for the same rationale.
  return (
    <>
      {sorted.length
        ? (
          <div>
            {sorted.map(task => (
              <WealthboxTaskRow
                key={task.id}
                task={task}
                onClick={() => handleRowClick(task)}
              />
            ))}
          </div>
        )
        : (
          <div className='p-md font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
            No tasks match the current filters.
          </div>
        )}

      <WealthboxTaskDetailModal
        open={!!selectedTask}
        onClose={handleCloseDetail}
        task={selectedTask}
        enableEdit={enableEdit}
        formMode={formMode}
        invalidateQueryKeys={TASK_INVALIDATE_KEYS}
      />

      <CreateWealthboxTaskModal
        open={createOpen}
        onClose={onCloseCreate}
        scope={scope}
        contactId={contactId}
        formMode={formMode}
        invalidateQueryKeys={TASK_INVALIDATE_KEYS}
      />
    </>
  )
}

WealthboxTasksContent.propTypes = {
  items: PropTypes.array,
  limit: PropTypes.number,
  config: PropTypes.object,
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  createOpen: PropTypes.bool,
  onCloseCreate: PropTypes.func
}

export default WealthboxTasksContent
